import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Câte opțiuni randăm maxim odată (pentru liste lungi -> foarte performant)
const MAX_RENDER = 80;

/**
 * Dropdown modern cu căsuță de căutare în interior.
 *
 * Props:
 *  - options: string[]
 *  - value: string
 *  - onChange: (val) => void
 *  - placeholder: string
 *  - required: boolean   (blochează trimiterea formularului fără selecție)
 *  - name: string
 */
const SearchableSelect = ({
  options = [],
  value = "",
  onChange,
  placeholder = "",
  required = false,
  name,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, query]);

  const visible = filtered.slice(0, MAX_RENDER);
  const hiddenCount = filtered.length - visible.length;

  // Închide când se dă click în afară
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Când se deschide: focus pe căutare (fără salt) + aducem panoul în vizor
  // (esențial pe mobil, unde panoul se deschidea sub ecran).
  useEffect(() => {
    if (open) {
      inputRef.current?.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        wrapperRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    } else {
      setQuery("");
    }
    setHighlight(0);
  }, [open]);

  // Ține opțiunea evidențiată în zona vizibilă la navigarea cu tastele
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const selectOption = (opt) => {
    onChange(opt);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (visible[highlight]) selectOption(visible[highlight]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Butonul (trigger) */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`mt-1 w-full flex items-center justify-between gap-2 rounded-xl border bg-white px-4 py-3 text-left text-[15px] transition-all duration-200 ${
          open
            ? "border-[#0066A6] ring-4 ring-[#0066A6]/10 shadow-sm"
            : "border-gray-200 hover:border-[#0066A6]/60 shadow-sm"
        }`}
      >
        <span className={`truncate ${value ? "text-[#003B5C] font-medium" : "text-gray-400"}`}>
          {value || placeholder}
        </span>
        <svg
          className={`ss-chevron h-5 w-5 shrink-0 text-gray-400 ${open ? "open" : ""}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Input ascuns pentru validarea nativă (required) */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="off"
          value={value}
          onChange={() => {}}
          required
          name={name}
          className="pointer-events-none absolute h-0 w-0 opacity-0"
          style={{ bottom: 6, left: "50%" }}
        />
      )}

      {open && (
        <div className="ss-panel absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl ring-1 ring-black/5">
          {/* Câmp de căutare */}
          <div className="p-2.5">
            <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200 focus-within:bg-white focus-within:ring-2 focus-within:ring-[#0066A6]/40 transition">
              <svg className="h-4 w-4 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="9" cy="9" r="6" />
                <path d="M17 17l-3.2-3.2" strokeLinecap="round" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlight(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={t("search_placeholder")}
                className="w-full bg-transparent text-sm text-[#003B5C] placeholder:text-gray-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Lista de opțiuni */}
          <ul ref={listRef} className="ss-scroll max-h-60 overflow-y-auto px-1.5 pb-2">
            {visible.length === 0 && (
              <li className="flex flex-col items-center gap-1 px-3 py-6 text-center">
                <svg className="h-6 w-6 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-3.5-3.5" strokeLinecap="round" />
                </svg>
                <span className="text-sm text-gray-400">{t("no_results")}</span>
              </li>
            )}
            {visible.map((opt, i) => {
              const selected = opt === value;
              const active = i === highlight;
              return (
                <li key={opt} data-idx={i}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => selectOption(opt)}
                    className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      active ? "bg-[#0066A6]/10" : ""
                    } ${selected ? "font-semibold text-[#0066A6]" : "text-[#003B5C]"}`}
                  >
                    <span className="truncate">{opt}</span>
                    {selected && (
                      <svg className="h-4 w-4 shrink-0 text-[#0066A6]" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 10.5l4 4 8-8.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
            {hiddenCount > 0 && (
              <li className="px-3 pt-2 pb-1 text-center text-xs text-gray-400">
                {t("more_results", { count: hiddenCount })}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
